import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
	ChangeDetectorRef,
} from "@angular/core";
import { Store } from "@ngrx/store";
import {
	AuthTransactionAction,
	AuthTransactionSelector,
	AuthenticationType,
	CoreState,
	VerificationStatus,
} from "@obg/ngx.core";
import { AuthTransactionTimingsService } from "@obg/ngx.core/src/auth-transaction/auth-transaction-timings.service";
import {
	CoreContentState,
	ImageContent,
	ImageSelector,
	TranslationGroup,
	TranslationSelector,
} from "@obg/ngx.core.content";
import {
	PlatformService,
	WindowRef,
	fromMillisecondsToMinutes,
	fromMillisecondsToSeconds,
	ignoreNil,
} from "@obg/ngx.global";
import { BehaviorSubject, Observable, Subscription, combineLatest, interval } from "rxjs";
import { delay, map, startWith, switchMap, take, tap } from "rxjs/operators";

import { REGISTRATION_BANKID_AUTH_TRANSLATIONS, ViewOptions } from "./bank-id-auth.model";

@Component({
	selector: "obg-bank-id-auth-container",
	templateUrl: "./bank-id-auth.container.html",
	styleUrls: ["./bank-id-auth.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class BankIdAuthContainer implements OnInit, OnDestroy {
	@Input() view = ViewOptions.default;
	@Input() bankIdAppNotOpenedTimeout = 5000;

	@Output() continueWithoutVerification = new EventEmitter<void>();
	@Output() notASwedishResident = new EventEmitter<void>();
	@Output() verified = new EventEmitter<void>();

	error: string | undefined;
	pollingTimeout = false;
	ssr = false;
	images$: Observable<Record<string, ImageContent | undefined>> | undefined;
	qrCode$: Observable<string> | undefined;
	qrCodeComponentTranslations$: Observable<Record<string, string | undefined>> | undefined;
	translations$: Observable<Record<string, string | undefined>> | undefined;

	private userCancelled = false;
	private data$$ = new Subscription();
	private deepLink$$ = new Subscription();

	private errorComingFrom$ = new BehaviorSubject<"qr-code-error" | "bank-id-start-error" | undefined>(undefined);

	get errorComingFromObservable$() {
		return this.errorComingFrom$.asObservable();
	}

	countdownInterval$: Observable<number> | undefined;

	constructor(
		private store: Store<CoreState & CoreContentState>,
		private authTransactionAction: AuthTransactionAction,
		private authTransactionSelector: AuthTransactionSelector,
		private imageSelector: ImageSelector,
		private platformService: PlatformService,
		private translationSelector: TranslationSelector,
		private windowRef: WindowRef,
		private changeDetector: ChangeDetectorRef,
		private authTimingsService: AuthTransactionTimingsService
	) {
		this.ssr = this.platformService.isServer;
		this.countdownInterval$ = this.authTimingsService.countdown$;
	}

	ngOnInit() {
		this.getTranslations();
		this.qrCode$ = this.getQrCode$();
		this.data$$.add(this.getStatus$$());
	}

	ngOnDestroy() {
		this.cancelPolling();
		this.data$$?.unsubscribe();
		this.deepLink$$?.unsubscribe();
	}

	extendTimeLeft() {
		this.authTimingsService.countdownInterval$.next(1000);
	}

	getQrCode$(): Observable<string> {
		return this.store.select(this.authTransactionSelector.getQrCode()).pipe(
			ignoreNil(),
			switchMap((qrCode) =>
				interval(1000).pipe(
					take(qrCode.length),
					map((i) => qrCode[i])
				)
			)
		);
	}

	getStatus$$(): Subscription {
		return this.store
			.select(this.authTransactionSelector.getLastStatus())
			.pipe(
				tap((status) => {
					if (status === VerificationStatus.Completed) {
						this.verified.emit();
					}
					if (status === VerificationStatus.Failed) {
						if (this.userCancelled) {
							this.userCancelled = false;
							return;
						}
						this.errorComingFrom$.next(
							this.view === ViewOptions.qr ? "qr-code-error" : "bank-id-start-error"
						);
						this.switchView(ViewOptions.polling);
						this.error = "bankid-not-responding-error";
						this.changeDetector.markForCheck();
					}
				})
			)
			.subscribe();
	}

	getTranslations() {
		this.translations$ = this.store.pipe(
			this.translationSelector.getByKeys_v2$(REGISTRATION_BANKID_AUTH_TRANSLATIONS.translations, [
				TranslationGroup.General,
				TranslationGroup.Common,
			]),
			ignoreNil(),
			map((keys) =>
				Object.fromEntries(keys.map((_, i) => [REGISTRATION_BANKID_AUTH_TRANSLATIONS.translations[i], keys[i]]))
			)
		);

		this.qrCodeComponentTranslations$ = this.authTimingsService.countdown$.pipe(
			startWith(0),
			switchMap((timeLeftMs) =>
				this.store.pipe(
					this.translationSelector.getByKeys_v2$(
						REGISTRATION_BANKID_AUTH_TRANSLATIONS.qrCodeComponentTranslations,
						[TranslationGroup.Common],
						{
							minutesLeft: Math.ceil(fromMillisecondsToMinutes(timeLeftMs)),
							secondsLeft: fromMillisecondsToSeconds(timeLeftMs),
						}
					),
					ignoreNil(),
					map((keys) =>
						Object.fromEntries(
							keys.map((_, i) => [
								REGISTRATION_BANKID_AUTH_TRANSLATIONS.qrCodeComponentTranslations[i],
								keys[i],
							])
						)
					)
				)
			)
		);

		this.images$ = combineLatest([
			this.store
				.select(
					this.imageSelector.getByKey(
						TranslationGroup.General,
						REGISTRATION_BANKID_AUTH_TRANSLATIONS.images[0]
					)
				)
				.pipe(ignoreNil()),
			this.store
				.select(
					this.imageSelector.getByKey(
						TranslationGroup.General,
						REGISTRATION_BANKID_AUTH_TRANSLATIONS.images[1]
					)
				)
				.pipe(ignoreNil()),
		]).pipe(
			map((images) =>
				Object.fromEntries(images.map((_, i) => [REGISTRATION_BANKID_AUTH_TRANSLATIONS.images[i], images[i]]))
			)
		);
	}

	onBankIdOnAnotherDeviceEvent() {
		this.clearErrors();
		this.startPolling(false);
		this.errorComingFrom$.next("qr-code-error");
		this.switchView(ViewOptions.qr);
	}

	onBankIdOpenEvent() {
		this.openBankId();
	}

	onCancelPollingEvent() {
		this.clearErrors();
		this.cancelPolling();
		this.switchView(ViewOptions.default);
	}

	onQrCodeCurrentDeviceEvent() {
		this.cancelLastVerification();
		this.clearErrors();
		this.startPolling(true);
		this.openBankIdApp();
		this.errorComingFrom$.next("bank-id-start-error");
		this.switchView(ViewOptions.polling);
	}

	onRetryBankIdEvent() {
		this.cancelLastVerification();
		this.clearErrors();
		this.startPolling(true);
		this.openBankIdApp();
		this.errorComingFrom$.next("bank-id-start-error");
		this.switchView(ViewOptions.polling);
	}

	private cancelPolling() {
		this.userCancelled = true;
		this.cancelLastVerification();
	}

	private clearErrors() {
		if (this.error) {
			this.error = undefined;
		}
	}

	private openBankIdApp() {
		this.pollingTimeout = false;
		this.deepLink$$?.unsubscribe();
		this.deepLink$$ = this.store
			.select(this.authTransactionSelector.getBankIdDeepLink())
			.pipe(
				ignoreNil(),
				take(1),
				tap((bankId) => (this.windowRef.native.location.href = bankId.deepLink)),
				delay(this.bankIdAppNotOpenedTimeout),
				tap(() => (this.pollingTimeout = true))
			)
			.subscribe();
	}

	private startPolling(userCancelled: boolean) {
		this.reset();
		this.userCancelled = userCancelled;
		this.store.dispatch(
			this.authTransactionAction.initializeAuthTransaction({
				shouldRememberUser: true,
				authenticationType: AuthenticationType.BankId,
				startBankIdTimings: true,
			})
		);
	}

	private switchView(view: ViewOptions) {
		this.view = view;
	}

	private reset() {
		this.store.dispatch(this.authTransactionAction.resetVerificationStatus());
	}

	private cancelLastVerification() {
		this.store.dispatch(
			this.authTransactionAction.cancelLastVerification({ authenticationType: AuthenticationType.BankId })
		);
	}

	private openBankId() {
		this.clearErrors();
		this.startPolling(false);
		this.openBankIdApp();
		this.errorComingFrom$.next("bank-id-start-error");
		this.switchView(ViewOptions.polling);
	}
}
